import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  useEffect(() => {
    fetchAll();
    api.get("/branches").then(({ data }) => setBranches(data.branches));
  }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="users-heading">Users</h1>
          <p className="text-sm text-slate-500 mt-1">Manage admin, branch and finance user accounts</p>
        </div>
        <button data-testid="add-user-button" onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-sm">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full" data-testid="users-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Branch</th><th>Created</th><th className="text-right pr-6">Actions</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-medium text-slate-900">{u.name}</td>
                <td>{u.email}</td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                    u.role === "admin" ? "bg-blue-100 text-blue-800" :
                    u.role === "finance" ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-800"
                  }`}>{u.role}</span>
                </td>
                <td>{u.branch || "—"}</td>
                <td className="text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                <td className="text-right pr-6">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}
                      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}
                      className="p-1.5 rounded-md hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showForm || editing) && (
        <UserForm initial={editing} branches={branches}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }} />
      )}
    </div>
  );
}

function UserForm({ initial, branches, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? {
    email: initial.email, name: initial.name, role: initial.role, branch: initial.branch || "", password: "",
  } : { email: "", name: "", role: "branch", branch: branches[0] || "", password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, role: form.role, branch: form.role === "branch" ? form.branch : null };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${initial.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", {
          email: form.email, password: form.password, name: form.name,
          role: form.role, branch: form.role === "branch" ? form.branch : null,
        });
        toast.success("User created");
      }
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="user-form-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="font-heading text-xl font-semibold mb-4">{isEdit ? "Edit User" : "New User"}</div>
        <div className="space-y-3">
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input" data-testid="uf-name" />
          </Field>
          <Field label="Email">
            <input required type="email" disabled={isEdit} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input disabled:bg-slate-100" data-testid="uf-email" />
          </Field>
          <Field label={isEdit ? "New Password (leave blank to keep)" : "Password"}>
            <input required={!isEdit} type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input" data-testid="uf-password" />
          </Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="input" data-testid="uf-role">
              <option value="admin">Admin</option>
              <option value="branch">Branch User</option>
              <option value="finance">Finance User</option>
              <option value="pdi">PDI (Pre-Delivery Inspection)</option>
            </select>
          </Field>
          {form.role === "branch" && (
            <Field label="Assigned Branch">
              <select value={form.branch || ""} onChange={(e) => setForm({ ...form, branch: e.target.value })}
                className="input" data-testid="uf-branch">
                {branches.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300" data-testid="uf-cancel">Cancel</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60" data-testid="uf-submit">
            {saving ? "Saving…" : (isEdit ? "Update" : "Create")}
          </button>
        </div>
        <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #CBD5E1; border-radius: 0.375rem; font-size: 0.875rem; outline: none; background: white; }`}</style>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
    {children}
  </label>
);
